import React, {useContext} from 'react';
import {Route, Redirect} from 'react-router-dom';

import {AuthContext} from 'context/AuthContext';
import {useFlags} from 'hooks/useFlags';
import {UserContext} from 'context/UserContext';
import LoadingPage from './LoadingPage';
import Unauthorized from 'pages/Unauthorized';

const signInRoute = '/signin';
const inviteRoute = '/invite';
const verifyEmailRoute = '/verify-email';
const defaultRegisteredRoute = '/onboard';
const defaultOnboardedRoute = '/home';
const unsubscribedRoute = '/unsubscribed';
const paymentFailedRoute = '/payment-failed';
const needsToCofirmPaymentRoute = 'app/confirm-payment';
const defaultSuspendedRoute = '/suspended';
const chooseSubscriptionRoute = '/choose-subscription';
const defaultNoUserRoute = '/user-404';

const PublicRoute = ({component: Component, render: RenderFunction, ...rest}) => {
    return (
        <Route
            {...rest}
            render={props => RenderFunction?.(props) ?? <Component {...props} />}
        />
    );
};

// Routes that you can't hit if you are authenticated
// i.e /signin
const UnAuthenticatedRoute = ({
    component: Component,
    render: RenderFunction,
    ...rest
}) => {
    const {isLoading} = useContext(AuthContext);
    const {userState, isUserLoading, subscription, isNewRegistration} =
        useContext(UserContext);
    const suspendedRoute = suspendedRoutePicker(subscription);
    const {onboard_wizard_active} = useFlags();

    return (
        <Route
            render={props => {
                if (isLoading || isUserLoading) {
                    return <LoadingPage />;
                } else if (userState === 'notOnTestList') {
                    return <Redirect to={inviteRoute} />;
                } else if (userState === 'emailNotVerified') {
                    return <Redirect to={verifyEmailRoute} />;
                } else if (userState === 'isUser') {
                    if (isNewRegistration && onboard_wizard_active) {
                        return <Redirect to={defaultRegisteredRoute} />;
                    } else {
                        return <Redirect to={defaultOnboardedRoute} />;
                    }
                } else if (userState === 'suspended') {
                    return <Redirect to={suspendedRoute} />;
                } else if (userState === 'error') {
                    return <Redirect to={defaultNoUserRoute} />;
                } else {
                    return RenderFunction?.(props) ?? <Component {...props} />;
                }
            }}
            {...rest}
        />
    );
};

// Routes you can hit when you are authenticated but not yet registered
// i.e /invite
const AuthenticatedRoute = ({
    component: Component,
    render: RenderFunction,
    ...rest
}) => {
    const {isLoading} = useContext(AuthContext);
    const {isUserLoading, userState, subscription, isNewRegistration} =
        useContext(UserContext);
    const suspendedRoute = suspendedRoutePicker(subscription);
    const {onboard_wizard_active} = useFlags();

    return (
        <Route
            render={props => {
                if (isUserLoading || isLoading) {
                    return <LoadingPage />;
                } else if (userState === 'suspended') {
                    return <Redirect to={suspendedRoute} />;
                } else if (userState === 'error') {
                    return <Redirect to={defaultNoUserRoute} />;
                } else if (userState === 'isUser') {
                    if (isNewRegistration && onboard_wizard_active) {
                        return <Redirect to={defaultRegisteredRoute} />;
                    } else {
                        return <Redirect to={defaultOnboardedRoute} />;
                    }
                } else {
                    // allowed for isAuthenticated and no authentication
                    return RenderFunction?.(props) ?? <Component {...props} />;
                }
            }}
            {...rest}
        />
    );
};

// Routes that you can hit only if you are registered, but not fully onboarded
// i.e /onboard/*
const RegisteredRoute = ({
    component: Component,
    render: RenderFunction,
    ...rest
}) => {
    const {isLoading} = useContext(AuthContext);
    const {isUserLoading, userState, subscription, isNewRegistration} =
        useContext(UserContext);
    const suspendedRoute = suspendedRoutePicker(subscription);
    const {onboard_wizard_active} = useFlags();

    return (
        <Route
            render={props => {
                if (isLoading || isUserLoading) {
                    return <LoadingPage />;
                } else if (userState === 'suspended') {
                    return <Redirect to={suspendedRoute} />;
                } else if (userState === 'error') {
                    return <Redirect to={defaultNoUserRoute} />;
                } else if (userState === 'isUser') {
                    if (isNewRegistration && onboard_wizard_active) {
                        return RenderFunction?.(props) ?? <Component {...props} />;
                    } else {
                        return <Redirect to={defaultOnboardedRoute} />;
                    }
                } else if (userState === 'emailNotVerified') {
                    return <Redirect to={verifyEmailRoute} />;
                } else {
                    return <Redirect to={signInRoute} />;
                }
            }}
            {...rest}
        />
    );
};

// Routes that you can hit only if you are fully authorized to use the site
// i.e /*
const OnboardedRoute = ({
    component: Component,
    render: RenderFunction,
    permission,
    ...rest
}) => {
    const {isLoading} = useContext(AuthContext);
    const {isUserLoading, userState, subscription, user, isNewRegistration} =
        useContext(UserContext);

    const suspendedRoute = suspendedRoutePicker(subscription);

    const {onboard_wizard_active} = useFlags();
    // add && false to cancel localhost bypass
    const isLocal =
        window.location.hostname === 'localhost' ||
        !!window.location.hostname.match('192.168');

    return (
        <Route
            render={props => {
                if (isLoading || isUserLoading) {
                    return <LoadingPage />;
                } else if (userState === 'suspended') {
                    return <Redirect to={suspendedRoute} />;
                } else if (userState === 'error') {
                    return <Redirect to={defaultNoUserRoute} />;
                } else if (userState === 'isUser' || isLocal) {
                    if (isNewRegistration && onboard_wizard_active) {
                        return <Redirect to={defaultRegisteredRoute} />;
                    } else if (permission) {
                        if (user?.permissions?.[permission]) {
                            return (
                                RenderFunction?.(props) ?? (
                                    <Component
                                        key={props?.location?.pathname}
                                        {...props}
                                    />
                                )
                            );
                        } else {
                            return <Unauthorized />;
                        }
                    } else {
                        return RenderFunction?.(props) ?? <Component {...props} />;
                    }
                } else if (userState === 'notOnTestList') {
                    // isAuthenticated) {
                    return <Redirect to={inviteRoute} />;
                } else {
                    return <Redirect to={signInRoute} />;
                }
            }}
            {...rest}
        />
    );
};

const suspendedRoutePicker = subscription => {
    const status = subscription?.subscription_status;

    if (status === null) {
        return chooseSubscriptionRoute;
    } else if (status === 'payment_failed') {
        return paymentFailedRoute;
    } else if (status === 'unsubscribed') {
        return unsubscribedRoute;
    } else if (status === 'needs_to_confirm_payment') {
        return needsToCofirmPaymentRoute;
    } else {
        return defaultSuspendedRoute;
    }
};

const AccountSuspendedRoute = ({
    component: Component,
    render: RenderFunction,
    ...rest
}) => {
    const {isLoading} = useContext(AuthContext);
    const {isUserLoading, userState, isNewRegistration} = useContext(UserContext);
    const {onboard_wizard_active} = useFlags();

    return (
        <Route
            render={props => {
                if (isLoading || isUserLoading) {
                    return <LoadingPage />;
                } else if (userState === 'suspended') {
                    return RenderFunction?.(props) ?? <Component {...props} />;
                } else if (userState === 'isUser') {
                    if (isNewRegistration && onboard_wizard_active) {
                        return <Redirect to={defaultRegisteredRoute} />;
                    } else {
                        return <Redirect to={defaultOnboardedRoute} />;
                    }
                } else if (userState === 'notOnTestList') {
                    // isAuthenticated
                    return <Redirect to={inviteRoute} />;
                } else if (userState === 'error') {
                    return <Redirect to={defaultNoUserRoute} />;
                } else {
                    return <Redirect to={signInRoute} />;
                }
            }}
            {...rest}
        />
    );
};

const NoUserRoute = ({component: Component, render: RenderFunction, ...rest}) => {
    const {isLoading} = useContext(AuthContext);
    const {isUserLoading, userState, isNewRegistration, subscription} =
        useContext(UserContext);
    const {onboard_wizard_active} = useFlags();

    const suspendedRoute = suspendedRoutePicker(subscription);

    return (
        <Route
            render={props => {
                if (isLoading || isUserLoading) {
                    return <LoadingPage />;
                } else if (userState === 'error') {
                    return RenderFunction?.(props) ?? <Component {...props} />;
                } else if (userState === 'suspended') {
                    return <Redirect to={suspendedRoute} />;
                } else if (userState === 'isUser') {
                    if (isNewRegistration && onboard_wizard_active) {
                        return <Redirect to={defaultRegisteredRoute} />;
                    } else {
                        return <Redirect to={defaultOnboardedRoute} />;
                    }
                } else if (userState === 'notOnTestList') {
                    // isAuthenticated
                    return <Redirect to={inviteRoute} />;
                } else if (userState === 'error') {
                    return <Redirect to={defaultNoUserRoute} />;
                } else {
                    return <Redirect to={signInRoute} />;
                }
            }}
            {...rest}
        />
    );
};

const LogoutRoute = ({component: Component, render: RenderFunction, ...rest}) => {
    const {isLoading} = useContext(AuthContext);
    const {isUserLoading, user} = useContext(UserContext);
    return (
        <Route
            render={props => {
                if (isLoading || isUserLoading) {
                    return <LoadingPage />;
                } else if (user) {
                    return RenderFunction?.(props) ?? <Component {...props} />;
                } else {
                    return <Redirect to='/' />;
                }
            }}
        />
    );
};

export {
    PublicRoute,
    UnAuthenticatedRoute,
    RegisteredRoute,
    AuthenticatedRoute,
    OnboardedRoute,
    AccountSuspendedRoute,
    LogoutRoute,
    NoUserRoute,
};
